import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  MenuSectionDeleteResp,
  MenuSectionDeleteVariables,
  MenuSection
} from "../../types/menuSectionTypes";

const DELETE_MENU_SECTION = gql`
  mutation DeleteMenuSection($attributes: DeleteMenuSectionInput!) {
    deleteMenuSection(attributes: $attributes) {
      menuSection {
        id
        name
        position
        menuId
      }
      errors
    }
  }
`   

type UseDeleteMenuSectionOptions = {
  onCompleted?: (menuSection: MenuSection) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<MenuSectionDeleteResp, MenuSectionDeleteVariables>>[1];
};

const useDeleteMenuSection = (options?: UseDeleteMenuSectionOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    MenuSectionDeleteResp,
    MenuSectionDeleteVariables
  >(DELETE_MENU_SECTION, options?.mutationOptions);

  const deleteMenuSection = useCallback(
    async (input: { id: number }) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!
            },
          },
        });

        const deleteMenuSection = result?.data?.deleteMenuSection;

        if (deleteMenuSection?.errors?.length) {
          throw new Error(deleteMenuSection.errors.join(", "));
        }

        if (options?.onCompleted && deleteMenuSection?.menuSection) {
          options.onCompleted(deleteMenuSection.menuSection);
        }

        return deleteMenuSection?.menuSection;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const menuSection = data?.deleteMenuSection?.menuSection;

  return {
    deleteMenuSection,
    deletedMenuSection: menuSection,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useDeleteMenuSection
